import {
  MenuItem,
  FormLabel,
  Stack,
  FormControl,
  Select,
  Typography,
  Tooltip,
  TextField,
} from '@mui/material';

import { SelectPlaceholder } from '@/components/selectPlaceholder';
import { Icon } from '@/components/shared/icon';
import { TextDetailsTuple } from '@/components/text/textDetailsTuple';

export type WriteMode = 'Provisioned' | 'On-demand';

export const DirectRestoreWriteModeSelector = ({
  writeMode,
  setWriteMode,
  writeCapacityUnits,
  setWriteCapacityUnits,
}: {
  writeMode: WriteMode;
  setWriteMode: (value: 'Provisioned' | 'On-demand') => void;
  writeCapacityUnits: number;
  setWriteCapacityUnits: (value: number) => void;
}) => {
  return (
    <>
      <FormControl size='small'>
        <Stack
          direction='row'
          justifyContent='space-between'
          alignItems='center'
        >
          <FormLabel>Select write mode</FormLabel>
        </Stack>
        <Select
          data-testid='writeModeSelect'
          size='small'
          value={writeMode}
          onChange={(event) => setWriteMode(event.target.value)}
          displayEmpty
          renderValue={(value) => {
            if (!value) {
              return <SelectPlaceholder />;
            }
            return <Typography variant='subtitle1'>{value}</Typography>;
          }}
        >
          <MenuItem key={'provisioned'} value={'Provisioned'}>
            <TextDetailsTuple
              primaryProp='Provisioned'
              secondaryProp="Uses your table's defined write capacity units."
              sameColor={writeMode === 'Provisioned'}
            />
          </MenuItem>
          <MenuItem key={'on-demand'} value={'On-demand'}>
            <TextDetailsTuple
              primaryProp='On-demand'
              secondaryProp='Uses on-demand write capacity units.'
              sameColor={writeMode === 'On-demand'}
            />
          </MenuItem>
        </Select>
      </FormControl>
      {writeMode === 'Provisioned' && (
        <FormControl size='small' className='group/capacity'>
          <Stack
            direction='row'
            justifyContent='space-between'
            alignItems='center'
          >
            <FormLabel>Write capacity units</FormLabel>
            <Tooltip
              title={
                <>
                  {
                    'During the restore process, write capacity units are temporarily increased to accelerate restoration.'
                  }
                  <br />
                  {
                    'Once the process completes, the capacity and billing mode are reverted to match the original table configuration.'
                  }
                </>
              }
              slotProps={{
                tooltip: {
                  sx: {
                    padding: '16px',
                  },
                },
              }}
            >
              <Icon
                className='opacity-0 group-hover/capacity:opacity-100 transition-opacity'
                iconClass='material-symbols-help-outline-rounded h-[18px] w-[18px] m-[0px]'
              />
            </Tooltip>
          </Stack>
          <TextField
            data-testid='databaseNameTextField'
            size='small'
            value={writeCapacityUnits || ''}
            type='number'
            slotProps={{ htmlInput: { step: '1', min: '0' } }}
            variant='outlined'
            onChange={(event) =>
              setWriteCapacityUnits(Number(event.target.value))
            }
          />
        </FormControl>
      )}
    </>
  );
};
